import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { FaCalendarAlt, FaUsers, FaFolderOpen, FaFileAlt } from 'react-icons/fa';
import {
  TechSection,
  Grid,
  Span,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  TechChip,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { RESEARCH_ITEMS, CATEGORY_META } from './data';

const tickStyle = { fontSize: 11, fontFamily: mono, fill: 'var(--text-disabled)' };
const tooltipStyle = {
  background: 'rgba(11, 19, 32, 0.95)',
  border: '1px solid rgba(0, 180, 216, 0.4)',
  borderRadius: 4,
  fontFamily: mono,
  fontSize: 12,
  color: 'var(--text-primary)',
};
const legendStyle = {
  paddingTop: 10,
  fontFamily: mono,
  fontSize: 11,
  letterSpacing: '0.04em',
};

const TopList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  counter-reset: tl;
`;

const TopItem = styled.li`
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 0.6rem;
  align-items: center;
  padding: 0.55rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  counter-increment: tl;
  font-size: 0.86rem;

  &::before {
    content: counter(tl, decimal-leading-zero);
    font-family: ${mono};
    color: ${accent};
    font-size: 0.7rem;
    letter-spacing: 0.08em;
  }

  .label { color: var(--text-primary); min-width: 0; }
  .count {
    font-family: ${mono};
    color: var(--text-disabled);
    font-size: 0.74rem;
  }
`;

const StudyRow = styled.li`
  display: grid;
  grid-template-columns: 26px 8px 1fr auto auto;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  margin-bottom: 0.4rem;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  counter-increment: sl;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:last-child { margin-bottom: 0; }

  &::before {
    content: counter(sl, decimal-leading-zero);
    font-family: ${mono};
    font-size: 0.66rem;
    color: ${accent};
    letter-spacing: 0.06em;
  }

  .swatch {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
  }

  .title {
    color: var(--text-primary);
    font-size: 0.86rem;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-disabled);
    white-space: nowrap;
  }

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
    transform: translateX(2px);
  }
`;

const StudyList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  counter-reset: sl;
`;

const ResearchCharts = ({ items, onOpenItem }) => {
  const itemsToShow = items && items.length > 0 ? items : RESEARCH_ITEMS;
  // datos derivados
  const byCategory = useMemo(() => {
    const counts = {};
    for (const it of itemsToShow) counts[it.category] = (counts[it.category] || 0) + 1;
    return Object.entries(counts).map(([k, v]) => ({
      name: CATEGORY_META[k]?.label || k,
      value: v,
      color: CATEGORY_META[k]?.color || accent,
    }));
  }, [itemsToShow]);

  const byType = useMemo(() => {
    const counts = {};
    for (const it of itemsToShow) counts[it.type] = (counts[it.type] || 0) + 1;
    return Object.entries(counts).map(([k, v]) => ({ name: k, value: v }));
  }, [itemsToShow]);

  const byMonth = useMemo(() => {
    const counts = {};
    for (const it of itemsToShow) {
      const d = new Date(it.date);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([k, v]) => ({
        name: new Date(k + '-01').toLocaleDateString('es-AR', { month: 'short', year: '2-digit' }),
        value: v,
      }));
  }, [itemsToShow]);

  const byAuthor = useMemo(() => {
    const counts = {};
    for (const it of itemsToShow) counts[it.author] = (counts[it.author] || 0) + 1;
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([k, v]) => ({ name: k, value: v }));
  }, [itemsToShow]);

  const topTags = useMemo(() => {
    const counts = {};
    for (const it of itemsToShow) {
      for (const t of it.tags || []) counts[t] = (counts[t] || 0) + 1;
    }
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);
  }, [itemsToShow]);

  const recent = useMemo(() => {
    return [...itemsToShow]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, 12);
  }, [itemsToShow]);

  return (
    <>
      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaFolderOpen /> Total estudios</StatLabel>
          <StatValue>{itemsToShow.length}</StatValue>
          <StatSub>indexados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaUsers /> Autores activos</StatLabel>
          <StatValue>{byAuthor.length}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Categorías</StatLabel>
          <StatValue>{byCategory.length}</StatValue>
          <StatSub>activas</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCalendarAlt /> Ventana</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>
            {byMonth.length > 0 ? `${byMonth[0].name} → ${byMonth[byMonth.length - 1].name}` : '—'}
          </StatValue>
        </StatTile>
      </StatStrip>

      <Grid>
        <Span span={8} className="mobile-full-width">
          <TechSection title="Producción mensual">
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={byMonth} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rcGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={accent} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                  <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="value" name="Investigaciones" stroke={accent} strokeWidth={2} fill="url(#rcGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </TechSection>

          <TechSection title="Investigaciones por categoría" style={{ marginTop: '1.25rem' }}>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCategory} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                  <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
                  <Bar dataKey="value" name="Investigaciones">
                    {byCategory.map((c, i) => (
                      <Cell key={i} fill={c.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TechSection>
        </Span>

        <Span span={4} className="mobile-full-width">
          <TechSection title="Distribución por tipo">
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={byType}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={86}
                    dataKey="value"
                    stroke="rgba(11, 19, 32, 0.7)"
                    strokeWidth={2}
                  >
                    {byType.map((_, i) => (
                      <Cell key={i} fill={['#00B4D8', '#06D6A0', '#FFD166', '#A4B0C2'][i % 4]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={legendStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </TechSection>

          <TechSection title="Top autores">
            <TopList>
              {byAuthor.slice(0, 5).map((a) => (
                <TopItem key={a.name}>
                  <span className="label">{a.name}</span>
                  <span className="count">{a.value}</span>
                </TopItem>
              ))}
            </TopList>
          </TechSection>

          <TechSection title="Top tags">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {topTags.map(([t, n]) => (
                <span
                  key={t}
                  style={{
                    fontFamily: mono,
                    fontSize: '0.72rem',
                    padding: '0.2rem 0.55rem',
                    border: '1px solid rgba(0, 180, 216, 0.25)',
                    borderRadius: 3,
                    color: '#cfe9f4',
                    background: 'rgba(0, 180, 216, 0.06)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  #{t} <span style={{ color: accent }}>{n}</span>
                </span>
              ))}
            </div>
          </TechSection>
        </Span>
      </Grid>

      <TechSection
        title={`Estudios recientes · ${recent.length}`}
        action={
          <span style={{
            fontFamily: mono,
            fontSize: '0.66rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--text-disabled)',
          }}>
            click para abrir
          </span>
        }
        style={{ marginTop: '1.25rem' }}
      >
        <StudyList>
          {recent.map((it) => {
            const cat = CATEGORY_META[it.category] || { color: accent, label: it.category };
            const fmt = (() => {
              const d = new Date(it.date);
              return isNaN(d) ? it.date : d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
            })();
            return (
              <StudyRow
                key={it.id}
                $color={cat.color}
                $clickable={Boolean(onOpenItem)}
                onClick={() => onOpenItem?.(it)}
              >
                <span className="swatch" />
                <span className="title" title={it.title}>
                  <FaFileAlt style={{ color: cat.color, fontSize: '0.78rem', marginRight: 6 }} />
                  {it.title}
                </span>
                <TechChip tone="mute">{it.type}</TechChip>
                <span className="meta">{fmt} · {it.author}</span>
              </StudyRow>
            );
          })}
        </StudyList>
      </TechSection>
    </>
  );
};

export default ResearchCharts;
